import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  Application,
  AzureStorageDocument,
  commercialEntityRoleType,
  GetApplicationPartiesPayload,
  individualRoleType,
  Metadata,
  defaultDocuments,
  unclassifiedDocuments,
  User, ConfirmationDialogResult, DEFAULT_LIMIT, DEFAULT_OFFSET,
  SelectableTagsDialogResult,
} from '@portal-workspace/grow-shared-library';
import {
  ApplicationDialogService,
  ApproveApplicationDocumentFn,
  DeclineApplicationDocumentFn,
  DeleteApplicationDocumentFn,
  DownloadApplicationDocumentUrlFn, 
  getUser, 
  openWindowAndDownloadWithFilename, 
  PortalHotToastService, 
  UndoApplicationDocumentFn, 
  UpdateApplicationDocumentTagsFn
} from '@portal-workspace/grow-ui-library';
import {isInternalUser} from '@portal-workspace/grow-shared-library';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';
import {tap} from 'rxjs/operators';
import {PageEvent} from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexModule } from '@angular/flex-layout/flex';
import { TagBoxComponent } from '../message-box/tag-box.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass, DatePipe } from '@angular/common';
import { MatTableModule } from '@angular/material/table';

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'document-details-sub-table',
    templateUrl: './document-details-sub-table.component.html',
    styleUrls: ['./document-details-sub-table.component.scss'],
    standalone: true,
    imports: [MatTableModule, NgClass, ExtendedModule, TagBoxComponent, FlexModule, MatTooltipModule, DatePipe]
})
export class DocumentDetailsSubTableComponent implements OnInit {
  subscriptions: Subscription[] = [];

  total = 0;
  limit = DEFAULT_LIMIT;
  offset = DEFAULT_OFFSET;

  @Input({required: false}) data: AzureStorageDocument[] = [];
  @Input({required: true}) application!: Application;
  /**
   * Parties from /get-application-parties, supplied by the parent rather than fetched here: one
   * instance of this component exists per expanded document group, across three tables.
   *
   * Null until the parent's fetch resolves, which only affects the tag dialog and the two entity-type
   * getters — the same window in which the legacy arrays were simply present, so callers below treat
   * null as "no parties" exactly as an empty array would have read.
   */
  @Input({required: false}) parties: GetApplicationPartiesPayload | null = null;
  @Input({required: true}) tagName!: string; // driversLicense ...
  @Input({required: true}) downloadApplicationDocumentUrlFn!: DownloadApplicationDocumentUrlFn;
  @Input({required: true}) approveApplicationDocumentFn!: ApproveApplicationDocumentFn;
  @Input({required: true}) declineApplicationDocumentFn!: DeclineApplicationDocumentFn;
  @Input({required: true}) deleteApplicationDocumentFn!: DeleteApplicationDocumentFn;
  @Input({required: true}) updateApplicationDocumentTagsFn!: UpdateApplicationDocumentTagsFn;
  @Input({required: true}) undoApplicationDocumentFn!: UndoApplicationDocumentFn;
  @Output() reload = new EventEmitter();
  defaultDocuments = defaultDocuments;
  isInternalUser = isInternalUser;
  unclassifiedDocuments = unclassifiedDocuments;

  displayedData: AzureStorageDocument[] = [];
  columnsToDisplay: string[] = ['icon', 'name', 'status', 'action'];
  user: User | null = null;
  constructor(
    private toastService: PortalHotToastService,
    private applicationDialogService: ApplicationDialogService
  ) { }

  ngOnInit(): void {
    this.user = getUser();
    this.displayedData = this.data;
  }

  private get primaryEntity() {
    return this.parties?.commercialEntities.find(entity => commercialEntityRoleType(entity) === 'Primary');
  }

  get isTrustApplication() {
    return this.primaryEntity?.entityType === 'TRST';
  }

  get isPartnershipApplication() {
    return this.primaryEntity?.entityType === 'PTNR';
  }

  updateDisplayedData() {
    this.displayedData = this.data.slice(this.offset * this.limit, (this.offset + 1) * this.limit);
  }

  onPagination($event: PageEvent) {
    this.offset = $event.pageIndex;
    this.limit = $event.pageSize;
    this.updateDisplayedData();
  }

  getColumnTitles(column: string): string {
    switch (column) {
      case 'name': return 'Document Uploaded';
      case 'action': return 'Actions';
      case 'status': return 'Status';
      default: return '';
    }
  }

  onDownloadDoc(doc: AzureStorageDocument) {
    this.downloadApplicationDocumentUrlFn(doc.name, "", 0).pipe(
      this.toastService.loadingWithMessage('Downloading...'),
      tap(blob => {
        openWindowAndDownloadWithFilename((doc.metadata as Metadata)?.['name'] ?? doc.name, blob)
      })
    ).subscribe()

  }

  onApproveDoc(doc: AzureStorageDocument) {
    this.subscriptions.push(
      this.approveApplicationDocumentFn(doc.name, "", this.tagName, this.user?.UserId).pipe(
        this.toastService.retryableMessage({
          successMessage: 'Document Approved',
          errorMessage: 'Failed to approve the document',
          retryFn: ()=> {
            console.log('**** retry ', this);
            this.onApproveDoc(doc);
          }
        }),
        tap(r => {
          this.reload.emit();
        })
      ).subscribe()
    )
  }

  onDeleteDoc(doc: AzureStorageDocument) {
    this.subscriptions.push(
      this.applicationDialogService.openConfirmationDialog({
        message: 'Please confirm',
        subMessage: 'Are you sure you want to delete ' + ((doc?.metadata as Metadata)['name'] ?? 'this file') + '?'
      }).afterClosed().pipe(
        tap(async (r: ConfirmationDialogResult | undefined) => {
          if (r && r.readyForSubmission) {
            this.deleteApplicationDocumentFn(doc.name, "", this.tagName).pipe(
              this.toastService.retryableMessage({
                successMessage: 'Document Delete',
                errorMessage: 'Failed to delete the document',
                retryFn: ()=> {
                  console.log('**** retry ', this);
                  this.onDeleteDoc(doc);
                }
              }),
              tap(r => {
                this.reload.emit();
              })
            ).subscribe()
          }
        })
      ).subscribe()
    );
  }

  onDeclineDoc(doc: AzureStorageDocument) {
    this.subscriptions.push(
      this.declineApplicationDocumentFn(doc.name, "", this.tagName, this.user?.UserId).pipe(
        this.toastService.retryableMessage({
          successMessage: 'Documents Declined',
          errorMessage: 'Failed to decline the documents',
          retryFn: ()=> {
            console.log('**** retry ', this);
            this.onDeclineDoc(doc);
          }
        }),
        tap(r => {
          this.reload.emit();
        })
      ).subscribe()
    )
  }

  onUndoDoc(doc: AzureStorageDocument) {
    this.subscriptions.push(
      this.undoApplicationDocumentFn(doc.name, "", this.tagName, this.user?.UserId).pipe(
        this.toastService.retryableMessage({
          successMessage: 'Undo Document',
          errorMessage: 'Failed to undo the document',
          retryFn: ()=> {
            console.log('**** retry ', this);
            this.onUndoDoc(doc);
          }
        }),
        tap(r => {
          this.reload.emit();
        })
      ).subscribe()
    )
  }

  onEditDoc(doc: AzureStorageDocument) {
    const individuals = this.parties?.individuals ?? [];
    // Through the legacy-role shims, not the role flags: `Role` collapses each individual's flags to
    // one string by precedence, so a director who also guarantees reads back as 'Director' and is
    // absent from this list. Reading isGuarantor instead would name more people in the tag dialog,
    // which is a behaviour change this migration is not making.
    const guarantorNames = individuals
      .filter(i => individualRoleType(i) === 'Guarantor')
      .map(i => `${i.givenName ?? ''} ${i.surname ?? ''}`);
    const applicant = individuals.find(i => individualRoleType(i) === 'Applicant');
    // The literal is deliberate. The legacy expression concatenated two `undefined` values when a
    // consumer application had no Applicant individual, tagging the dialog "undefined undefined", and
    // this migration changes only where the data comes from. Recorded in the working notes.
    const applicantName = applicant
      ? `${applicant.givenName ?? ''} ${applicant.surname ?? ''}`
      : 'undefined undefined';
    this.subscriptions.push(
      this.applicationDialogService.openSelectableTagsDialog({
        fileName: (doc?.metadata as Metadata)['name'],
        previouslySelectedTags: Object.values(doc?.tags ?? []),
        allTags: this.application?.DocumentTypes?.length ? this.application?.DocumentTypes
          : [...this.defaultDocuments(
            this.application.ApplicationType,
            guarantorNames,
            this.application.ApplicationType === "Consumer" ? applicantName : (this.primaryEntity?.legalName ?? ""),
            "",
            "",
            (this.application.PricingDetails as any).LoanAmount,
            (this.application.PricingDetails as any).Deposit,
            (this.application.PricingDetails as any).PrivateSale === "Yes",
            this.isTrustApplication,
            this.isPartnershipApplication
          ), this.unclassifiedDocuments()]
      }).afterClosed().pipe(
        tap((r: SelectableTagsDialogResult | undefined) => {
          if (r && r.valid) {
            console.log(r);
            this.updateApplicationDocumentTagsFn(doc.name, '', r.selectedTags.map(tag => tag.value)).pipe(
              this.toastService.retryableMessage({
                successMessage: 'Document Tags Updated',
                errorMessage: 'Failed to update the tags',
                retryFn: ()=> {
                  console.log('**** retry ', this);
                  this.onEditDoc(doc);
                }
              }),
              tap(r => {
                this.reload.emit();
              })
            ).subscribe()
          }
        })
      ).subscribe()
    );
  }
}
